"use client";

import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageCircle, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
}
const faqData: FaqItem[] = [{
  id: "item-1",
  question: "¿Cómo integro los hongos funcionales en mi rutina diaria?",
  answer: "Puedes comenzar tu mañana con la crema facial Maitake y unas gotas de té o tintura en tu infusión preferida. Por la noche, la crema corporal con Reishi relaja tu piel antes de descansar. Cada producto está pensado para complementarse sin complicaciones."
}, {
  id: "item-2",
  question: "¿Cuánto tarda en llegar mi pedido en México?",
  answer: "Los envíos en la Ciudad de México y área metropolitana toman entre 1 y 2 días hábiles. Para el resto de la República Mexicana, el tiempo estimado de entrega es de 3 a 5 días hábiles mediante paquetería exprés con número de rastreo."
}, {
  id: "item-3",
  question: "¿Cómo funciona el envío gratis?",
  answer: "Ofrecemos envío estándar sin costo en todas las compras a partir de $1,999 MXN o al adquirir el Kit completo de ritual. El descuento se aplica automáticamente al momento de calcular tu compra."
}, {
  id: "item-4",
  question: "¿Son productos aptos para todo tipo de piel?",
  answer: "Sí, formulamos con extractos botánicos puros e hidratantes suaves como manteca vegetal y ácido hialurónico natural. Son ideales para pieles sensibles, secas o con rojeces gracias a las propiedades calmantes del Reishi y Maitake."
}, {
  id: "item-5",
  question: "¿Qué formas de pago aceptan?",
  answer: "Aceptamos tarjetas de débito y crédito (Visa, Mastercard, American Express), pagos seguros con Mercado Pago, PayPal y transferencias bancarias vía SPEI."
}];
export default function Faq() {
  return <section id="preguntas-frecuentes" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto max-w-4xl px-5 sm:px-8">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-widest mb-4 border border-border">
            <Sparkles className="size-3.5" />
            <span>Respuestas claras</span>
          </div>

          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-foreground mb-4">
            Preguntas frecuentes
          </h2>

          <p className="text-muted-foreground text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            Todo lo que necesitas saber sobre el uso de hongos funcionales, tiempos de envío y el ritual de bienestar diario.
          </p>
        </motion.div>

        {/* Accordion Container */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: [0.22, 1, 0.36, 1]
      }}>
          <Accordion type="single" collapsible className="w-full space-y-3.5">
            {faqData.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="bg-card text-card-foreground border border-border rounded-2xl px-5 sm:px-7 py-1 shadow-xs transition-colors hover:border-primary/40 overflow-hidden">
                <AccordionTrigger className="hover:no-underline py-5 text-left text-base sm:text-lg font-serif font-medium text-foreground cursor-pointer gap-4">
                  <span className="flex items-center gap-3">
                    <HelpCircle className="size-4.5 text-primary shrink-0 opacity-80" />
                    <span>{faq.question}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 pl-7.5 pr-2">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        {/* Reassurance & Support Callout */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.45,
        delay: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-12 md:mt-16 text-center bg-secondary text-secondary-foreground rounded-2xl p-6 sm:p-8 border border-border flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-center sm:text-left">
            <h3 className="font-serif text-lg md:text-xl font-medium text-foreground mb-1">
              ¿Tienes alguna duda sobre tu piel o el ritual?
            </h3>
            <p className="text-muted-foreground text-sm max-w-md">
              Escríbenos por WhatsApp o correo. Te asesoramos de lunes a domingo de 9:00 a 20:00 hrs.
            </p>
          </div>

          <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-2.5 shadow-sm shrink-0 font-medium cursor-pointer">
            <Link to="https://wa.me/5639561232" newTab aria-label="Contactar a Spora por WhatsApp">
              <MessageCircle className="size-4 mr-1.5" />
              <span>Hablar con un asesor</span>
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}